import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import type { Map as MapLibreMap, LngLatBounds } from 'maplibre-gl'
import type { Coordinates, Tweet, TweetFilters, LayerSet, LayerInstance } from '@/types'
import { normalizeUrlKey } from '@/utils/url'

interface AppState {
  // Initialization
  isInitialized: boolean
  setInitialized: (value: boolean) => void

  // Map state
  map: {
    instance: MapLibreMap | null
    center: Coordinates
    zoom: number
    bounds: LngLatBounds | null
  }
  setMapInstance: (map: MapLibreMap | null) => void
  setMapView: (center: Coordinates, zoom: number) => void
  setMapBounds: (bounds: LngLatBounds) => void

  // Drawn shapes as a GeoJSON string (or remote GeoJSON URL) for URL sharing
  drawings: string | null
  setDrawings: (data: string | null) => void
  // Drawings stashed when entering story view (posts replace the drawings,
  // exitStoryView restores these)
  drawingsBefore: string | null

  // Layer state
  layers: {
    visible: string[]
    baseLayer: string
    sets: LayerSet[]
    instances: Map<string, LayerInstance>
  }
  setVisibleLayers: (layers: string[]) => void
  toggleLayer: (layerId: string) => void
  setBaseLayer: (layerId: string) => void

  // Tweet state
  tweets: {
    data: Map<string, Tweet>
    urlToTweetId: Map<string, string> // URL → tweet ID mapping for URL-based activation
    visibleIds: string[]
    activeTweetId: string | null
    activeStoryId: string | null
    hoverTweetId: string | null
    selectedTweetId: string | null
    scrollToTweetId: string | null
    filters: TweetFilters
    filterByBounds: boolean
    frozenBounds: LngLatBounds | null
    pagination: {
      currentPage: number
      perPage: number
    }
    viewMode: 'overview' | 'story'
    currentStoryIndex: number
    overviewPage: number
  }
  setTweets: (tweets: Tweet[]) => void
  setVisibleTweetIds: (ids: string[]) => void
  selectTweet: (id: string | null) => void
  selectStory: (id: string | null) => void
  hoverTweet: (id: string | null) => void
  selectTweetForHighlight: (id: string | null) => void
  scrollToTweet: (id: string | null) => void
  setFilter: (type: keyof TweetFilters, value: string | null) => void
  setFilterByBounds: (value: boolean, frozenBounds?: LngLatBounds | null) => void
  setPage: (page: number) => void
  setViewMode: (mode: 'overview' | 'story') => void
  setStoryIndex: (index: number) => void
  setOverviewPage: (page: number) => void
  enterStoryView: (tweetId: string) => void
  exitStoryView: () => void

  // UI state
  ui: {
    sidebarTab: 'messages' | 'layers'
    bottomSheetHeight: number // percentage of viewport height
    storyContentHeight: number | null // content height in story mode (vh)
    isMobile: boolean
    isPreview: boolean
    closePopups: number // increment to trigger popup closing
    overviewMode: 'messages' | 'layers' // overview box mode
  }
  setSidebarTab: (tab: 'messages' | 'layers') => void
  setBottomSheetHeight: (height: number) => void
  setStoryContentHeight: (height: number | null) => void
  setIsMobile: (isMobile: boolean) => void
  setIsPreview: (isPreview: boolean) => void
  closeAllPopups: () => void
  setOverviewMode: (mode: 'messages' | 'layers') => void

  // State restoration (for back navigation)
  stateBefore: {
    center: Coordinates
    zoom: number
    page?: number
  } | null
  setStateBefore: (state: { center: Coordinates; zoom: number; page?: number } | null) => void
}

export const useStore = create<AppState>()(
  devtools(
    subscribeWithSelector((set) => ({
      // Initialization
      isInitialized: false,
      setInitialized: (value) => set({ isInitialized: value }),

      // Map state
      map: {
        instance: null,
        center: { lat: 48.2082, lng: 16.3738 }, // Vienna default
        zoom: 5,
        bounds: null,
      },
      setMapInstance: (instance) =>
        set((state) => ({
          map: { ...state.map, instance },
        })),
      setMapView: (center, zoom) =>
        set((state) => ({
          map: { ...state.map, center, zoom },
        })),
      setMapBounds: (bounds) =>
        set((state) => ({
          map: { ...state.map, bounds },
        })),

      // Drawings state
      drawings: null,
      setDrawings: (drawings) => set({ drawings }),
      drawingsBefore: null,

      // Layer state
      layers: {
        visible: ['satellite'],
        baseLayer: 'satellite',
        sets: [],
        instances: new Map(),
      },
      setVisibleLayers: (visible) =>
        set((state) => ({
          layers: { ...state.layers, visible },
        })),
      toggleLayer: (layerId) =>
        set((state) => {
          const visible = state.layers.visible.includes(layerId)
            ? state.layers.visible.filter((id) => id !== layerId)
            : [...state.layers.visible, layerId]
          return { layers: { ...state.layers, visible } }
        }),
      setBaseLayer: (layerId) =>
        set((state) => ({
          layers: { ...state.layers, baseLayer: layerId },
        })),

      // Tweet state
      tweets: {
        data: new Map(),
        urlToTweetId: new Map(),
        visibleIds: [],
        activeTweetId: null,
        activeStoryId: null,
        hoverTweetId: null,
        selectedTweetId: null,
        scrollToTweetId: null,
        filters: {
          account: null,
          hashtag: null,
        },
        filterByBounds: false,
        frozenBounds: null,
        pagination: {
          currentPage: 1,
          perPage: 10,
        },
        viewMode: 'overview',
        currentStoryIndex: 0,
        overviewPage: 1,
      },
      setTweets: (tweets) =>
        set((state) => {
          const data = new Map(state.tweets.data)
          const urlToTweetId = new Map(state.tweets.urlToTweetId)
          tweets.forEach((tweet) => {
            data.set(tweet.id, tweet)
            // Build URL → tweet ID mapping for URL-based activation
            if (tweet.expandedUrl) {
              urlToTweetId.set(normalizeUrlKey(tweet.expandedUrl), tweet.id)
            }
          })
          return { tweets: { ...state.tweets, data, urlToTweetId } }
        }),
      setVisibleTweetIds: (visibleIds) =>
        set((state) => ({
          tweets: { ...state.tweets, visibleIds },
        })),
      selectTweet: (activeTweetId) =>
        set((state) => ({
          tweets: { ...state.tweets, activeTweetId },
        })),
      selectStory: (activeStoryId) =>
        set((state) => ({
          tweets: { ...state.tweets, activeStoryId },
        })),
      hoverTweet: (hoverTweetId) =>
        set((state) => ({
          tweets: { ...state.tweets, hoverTweetId },
        })),
      selectTweetForHighlight: (selectedTweetId) =>
        set((state) => ({
          tweets: { ...state.tweets, selectedTweetId },
        })),
      scrollToTweet: (scrollToTweetId) =>
        set((state) => ({
          tweets: { ...state.tweets, scrollToTweetId },
        })),
      setFilter: (type, value) =>
        set((state) => ({
          tweets: {
            ...state.tweets,
            filters: { ...state.tweets.filters, [type]: value },
            pagination: { ...state.tweets.pagination, currentPage: 1 },
          },
        })),
      setFilterByBounds: (filterByBounds, frozenBounds) =>
        set((state) => ({
          tweets: {
            ...state.tweets,
            filterByBounds,
            frozenBounds: frozenBounds !== undefined ? frozenBounds : state.tweets.frozenBounds,
            pagination: { ...state.tweets.pagination, currentPage: 1 },
          },
        })),
      setPage: (page) =>
        set((state) => ({
          tweets: {
            ...state.tweets,
            pagination: { ...state.tweets.pagination, currentPage: page },
          },
        })),
      setViewMode: (viewMode) =>
        set((state) => ({
          tweets: { ...state.tweets, viewMode },
        })),
      setStoryIndex: (currentStoryIndex) =>
        set((state) => ({
          tweets: { ...state.tweets, currentStoryIndex },
        })),
      enterStoryView: (tweetId) =>
        set((state) => ({
          // Stash the drawings when coming from the overview; while in story
          // view the map mirrors the active post's shapes instead
          ...(state.tweets.viewMode !== 'story' ? { drawingsBefore: state.drawings } : {}),
          tweets: {
            ...state.tweets,
            activeTweetId: tweetId,
            viewMode: 'story',
            currentStoryIndex: 0,
          },
        })),
      exitStoryView: () =>
        set((state) => ({
          drawings: state.drawingsBefore,
          tweets: {
            ...state.tweets,
            activeTweetId: null,
            viewMode: 'overview',
            currentStoryIndex: 0,
          },
        })),
      setOverviewPage: (overviewPage) =>
        set((state) => ({
          tweets: { ...state.tweets, overviewPage },
        })),

      // UI state
      ui: {
        sidebarTab: 'messages',
        bottomSheetHeight: 50,
        storyContentHeight: null,
        isMobile: false,
        isPreview: false,
        closePopups: 0,
        overviewMode: 'messages',
      },
      setSidebarTab: (sidebarTab) =>
        set((state) => ({
          ui: { ...state.ui, sidebarTab },
        })),
      setBottomSheetHeight: (bottomSheetHeight) =>
        set((state) => ({
          ui: { ...state.ui, bottomSheetHeight },
        })),
      setStoryContentHeight: (storyContentHeight) =>
        set((state) => ({
          ui: { ...state.ui, storyContentHeight },
        })),
      setIsMobile: (isMobile) =>
        set((state) => ({
          ui: { ...state.ui, isMobile },
        })),
      setIsPreview: (isPreview) =>
        set((state) => ({
          ui: { ...state.ui, isPreview },
        })),
      closeAllPopups: () =>
        set((state) => ({
          ui: { ...state.ui, closePopups: state.ui.closePopups + 1 },
        })),
      setOverviewMode: (overviewMode) =>
        set((state) => ({
          ui: { ...state.ui, overviewMode },
        })),

      // State restoration
      stateBefore: null,
      setStateBefore: (stateBefore) => set({ stateBefore }),
    }))
  )
)
